// src/data/memberMessages.js - task list section 776. Bob 2026-10-22 14:53-14:58 ET accepted the recommendation: messaging on
// the platform is the primary channel between a member and his own Local and General Chairperson; e-mail shrinks to a
// notice with no content (src\data\platformEmail.js). PURE: no Firestore, no network, no clock. The callable memberMessage
// (functions\index.js) is the only writer; firestore.rules refuses every client write and reads as mayRead does here.
//
// ONE THREAD PER MEMBER, messageThreads/{memberUid}, addressed by his own territory and committee (the scope and committee
// his account carries - the walls claims are read by). It is between him and the offices that represent him:
//   - the member, on his own thread;
//   - the Local Chairperson whose scopes hold the thread's territory;
//   - the General Chairperson of the thread's committee;
//   - the administrator.
// Nobody else reads it; no other committee, no other member. Messages are a permanent record: never edited, never deleted.
// An officer answers a thread a member opened; he does not open one (a claim's own channel, ruling 29, covers claim matters).

export const MAX_BODY = 4000;
export const NOTICE_KIND = 'member_message';
const s = (v) => (v == null ? '' : String(v)).trim();

// The address a member's thread carries: his committee and his territory, from his account's claims.
export function threadAddress(member) {
  const m = member || {};
  const scopes = Array.isArray(m.scopes) ? m.scopes.map(s).filter(Boolean) : [];
  return { committee: s(m.committee), scopeKey: scopes.length === 1 ? scopes[0] : '' };
}

// Who may read (and so answer) a thread. caller = { uid, role, committee, scopes[], isAdmin }.
export function mayRead(caller, thread) {
  const a = caller || {}, t = thread || {};
  if (!s(a.uid)) return { may: false, because: 'Sign in first.' };
  if (a.isAdmin || s(a.role) === 'admin') return { may: true, side: 'admin' };
  if (s(t.memberUid) && s(t.memberUid) === s(a.uid)) return { may: true, side: 'member' };
  if (s(a.role) === 'local_chair' && s(t.scopeKey) && Array.isArray(a.scopes) && a.scopes.includes(s(t.scopeKey))) return { may: true, side: 'local_chair' };
  if (s(a.role) === 'general_chair' && s(a.committee) && s(a.committee) === s(t.committee)) return { may: true, side: 'general_chair' };
  return { may: false, because: 'This conversation is between a member and the officers who represent him.' };
}

// Who may post. A member posts on his own thread (it is opened on his first message); an officer answers an existing thread
// he may read. A member account with no committee has no office to write to, and is told so.
export function mayPost(caller, thread) {
  const a = caller || {};
  if (!s(a.uid)) return { may: false, because: 'Sign in first.' };
  if (!thread) {
    if (s(a.role) !== 'member' && s(a.role) !== 'tester') return { may: false, because: 'An officer answers a conversation a member has opened.' };
    if (!s(a.committee)) return { may: false, because: 'Your account is not yet attached to a General Committee, so there is no office to write to. Ask the officer who approved you.' };
    return { may: true, side: 'member', opens: true };
  }
  const r = mayRead(a, thread);
  return r.may ? { may: true, side: r.side, opens: false } : r;
}

export function validateBody(body) {
  const t = String(body == null ? '' : body).replace(/\r\n?/g, '\n').replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, '').trim();   // eslint-disable-line no-control-regex
  if (!t) return { ok: false, why: 'Write your message first.' };
  if (t.length > MAX_BODY) return { ok: false, why: 'A message is at most ' + MAX_BODY + ' characters. Send it in two parts.' };
  return { ok: true, value: t };
}

const SIDE_WORDS = { member: 'Member', local_chair: 'Local Chairperson', general_chair: 'General Chairperson', admin: 'Administrator' };
export function sideWords(side) { return SIDE_WORDS[side] || 'Member'; }

// The writes one post makes, as data. thread is the existing thread or null; member is { uid, name, committee, scopes }
// when the member opens it. Returns { thread, message, notices, nudge } - nudge lists who is owed an e-mail notice
// (each side at most once until it reads the thread: the 'nudged' marks are cleared by markRead).
export function planPost({ thread, caller, member, body, at }) {
  const c = caller || {};
  const side = (mayPost(c, thread) || {}).side || 'member';
  const t = thread ? Object.assign({}, thread) : Object.assign({ memberUid: s(c.uid), memberName: s((member || {}).name) || s(c.name), createdAt: at, count: 0,
    readBy: {}, nudged: {} }, threadAddress(member || c));
  const message = { memberUid: t.memberUid, committee: t.committee, scopeKey: t.scopeKey, fromUid: s(c.uid), fromName: s(c.name) || sideWords(side),
    fromSide: side, body: s(body), sentAt: at };
  t.count = (Number(t.count) || 0) + 1;
  t.lastMessageAt = at;
  t.lastFromSide = side;
  t.readBy = Object.assign({}, t.readBy || {}, { [s(c.uid)]: at });   // the writer has read his own message
  const nudged = Object.assign({}, t.nudged || {});
  const notices = [];
  const nudge = [];
  const base = { kind: NOTICE_KIND, claimId: '', channel: 'in_app', readAt: null, createdAt: at, actorName: message.fromName, actorRole: side };
  if (side === 'member') {
    const routed = t.scopeKey && !/\|UNROUTED$/.test(t.scopeKey);
    if (routed) notices.push(Object.assign({}, base, { audience: 'local_chair', recipientUid: null, scopeKey: t.scopeKey, committee: t.committee,
      title: 'New message from ' + t.memberName, body: 'Open Messages from the menu to read it and answer.' }));
    if (t.committee) notices.push(Object.assign({}, base, { audience: 'general_chair', recipientUid: null, scopeKey: t.scopeKey, committee: t.committee,
      title: 'New message from ' + t.memberName, body: 'Open Messages from the menu to read it and answer.' }));
    if (routed && !nudged.local_chair) { nudge.push({ to: 'local_chair', officeId: t.scopeKey }); nudged.local_chair = true; }
    if (t.committee && !nudged.general_chair) { nudge.push({ to: 'general_chair', officeId: t.committee }); nudged.general_chair = true; }
  } else {
    notices.push(Object.assign({}, base, { audience: 'claimant', recipientUid: t.memberUid, scopeKey: t.scopeKey, committee: t.committee,
      title: 'New message from your ' + sideWords(side), body: 'Open Messages from the menu to read it.' }));
    if (!nudged.member) { nudge.push({ to: 'member', uid: t.memberUid }); nudged.member = true; }
  }
  t.nudged = nudged;
  return { thread: t, message, notices, nudge };
}

// Reading a thread records the reader and clears his side's e-mail mark, so the next message may send one notice again.
export function planRead({ thread, caller, at }) {
  const r = mayRead(caller, thread);
  if (!r.may) return r;
  const nudged = Object.assign({}, (thread || {}).nudged || {});
  delete nudged[r.side === 'admin' ? '__none__' : r.side];
  return { may: true, readBy: Object.assign({}, (thread || {}).readBy || {}, { [s(caller.uid)]: at }), nudged };
}

// "Read by ..." for the screen: who on the other side has read the thread since its last message.
export function readLine(thread, officers) {
  const t = thread || {};
  const last = s(t.lastMessageAt);
  const names = [];
  for (const [uid, when] of Object.entries(t.readBy || {})) {
    if (uid === s(t.memberUid) || !last || s(when) < last) continue;
    names.push((officers && officers[uid]) || 'an officer');
  }
  return names;
}
